import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  GeneralDefaultSettingData,
  generalDefaultSettingApi,
  LookupItem,
} from '../api/generalDefaultSettingApi';

const GeneralDefaultSettingPage: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [shiftOptions, setShiftOptions] = React.useState<LookupItem[]>([]);
  const [workWeekOptions, setWorkWeekOptions] = React.useState<LookupItem[]>([]);
  const [departmentOptions, setDepartmentOptions] = React.useState<LookupItem[]>([]);
  const [employeeOptions, setEmployeeOptions] = React.useState<LookupItem[]>([]);

  const [form, setForm] = React.useState<GeneralDefaultSettingData>({
    shiftId: undefined,
    workWeekId: undefined,
    departmentId: undefined,
    attendanceSupervisorId: undefined,
    leaveSupervisorId: undefined,
    isActive: true,
  });

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await generalDefaultSettingApi.get();

      setShiftOptions(data.shiftOptions || []);
      setWorkWeekOptions(data.workWeekOptions || []);
      setDepartmentOptions(data.departmentOptions || []);
      setEmployeeOptions(data.employeeOptions || []);

      if (data.setting) {
        setForm({
          id: data.setting.id,
          shiftId: data.setting.shiftId,
          workWeekId: data.setting.workWeekId,
          departmentId: data.setting.departmentId,
          attendanceSupervisorId: data.setting.attendanceSupervisorId,
          leaveSupervisorId: data.setting.leaveSupervisorId,
          isActive: data.setting.isActive,
        });
      }
    } catch {
      toast.error('Failed to load General Default setting data');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!form.shiftId || !form.workWeekId || !form.departmentId || !form.attendanceSupervisorId || !form.leaveSupervisorId) {
      toast.error('Please select and fill all fields.');
      return;
    }
    setSaving(true);
    try {
      await generalDefaultSettingApi.save(form);
      toast.success('General Default setting saved successfully');
      await loadData();
    } catch {
      toast.error('Failed to save General Default setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>
        General Default Setting
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {loading ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={3} maxWidth={640}>
            <Typography variant="h6" color="text.secondary" fontWeight={500}>
              Default Settings
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Shift</InputLabel>
              <Select
                label="Shift"
                value={form.shiftId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, shiftId: e.target.value || undefined }))}
              >
                <MenuItem value="">Select Shift</MenuItem>
                {shiftOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Work Week</InputLabel>
              <Select
                label="Work Week"
                value={form.workWeekId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, workWeekId: e.target.value || undefined }))}
              >
                <MenuItem value="">Select Work Week</MenuItem>
                {workWeekOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={form.departmentId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, departmentId: e.target.value || undefined }))}
              >
                <MenuItem value="">Select Department</MenuItem>
                {departmentOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Attendance Supervisor</InputLabel>
              <Select
                label="Attendance Supervisor"
                value={form.attendanceSupervisorId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, attendanceSupervisorId: e.target.value || undefined }))}
              >
                <MenuItem value="">Select Attendance Supervisor</MenuItem>
                {employeeOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Leave Supervisor</InputLabel>
              <Select
                label="Leave Supervisor"
                value={form.leaveSupervisorId ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, leaveSupervisorId: e.target.value || undefined }))}
              >
                <MenuItem value="">Select Leave Supervisor</MenuItem>
                {employeeOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />

            <Box textAlign="right">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  saving ||
                  loading ||
                  !form.shiftId ||
                  !form.workWeekId ||
                  !form.departmentId ||
                  !form.attendanceSupervisorId ||
                  !form.leaveSupervisorId
                }
                sx={{ textTransform: 'none' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default GeneralDefaultSettingPage;
